/**
 * Mock Organization Data
 * Contains minimal sample data for companies, consultancies, and certification bodies
 */

import type {
  Organization,
  Company,
  Consultancy,
  CertificationBody,
} from '../types/organization';
import {
  OrganizationType,
  OrganizationStatus,
  IndustrySector,
} from '../types/organization';

/**
 * Mock Company
 */
export const mockCompany: Company = {
  id: 'org-001',
  name: 'Nasi Lemak Food Industries Sdn Bhd',
  type: OrganizationType.COMPANY,
  status: OrganizationStatus.ACTIVE,
  registrationNumber: '202301234567',
  industry: IndustrySector.FOOD_MANUFACTURING,
  yearEstablished: 2023,
  numberOfEmployees: 45,
  primaryContact: {
    name: 'Ahmad bin Abdullah',
    position: 'Operations Director',
    email: 'ahmad@nasilemakfood.com.my',
    phone: '+60123456789',
    mobile: '+60123456789',
  },
  businessAddress: {
    street: 'Lot 123, Jalan Perindustrian 5',
    city: 'Shah Alam',
    state: 'Selangor',
    postalCode: '40000',
    country: 'Malaysia',
  },
  isCertified: false,
  readinessScore: 72,
  readinessAssessmentDate: new Date('2025-12-15'),
  assignedConsultantId: 'user-003',
  consultantAssignedDate: new Date('2025-12-20'),
  createdAt: new Date('2025-11-01'),
  updatedAt: new Date('2025-12-20'),
  createdBy: 'user-001',
};

/**
 * Mock Consultancy
 */
export const mockConsultancy: Consultancy = {
  id: 'org-002',
  name: 'Halal Excellence Consultancy',
  type: OrganizationType.CONSULTANCY,
  status: OrganizationStatus.ACTIVE,
  registrationNumber: '202201987654',
  industry: IndustrySector.FOOD_MANUFACTURING,
  yearEstablished: 2022,
  primaryContact: {
    name: 'Dr. Siti Aminah',
    position: 'Managing Director',
    email: 'siti@halalexcellence.com.my',
    phone: '+60198765432',
    mobile: '+60198765432',
  },
  businessAddress: {
    street: 'Suite 15-3, Menara IGB',
    city: 'Kuala Lumpur',
    state: 'Wilayah Persekutuan',
    postalCode: '50450',
    country: 'Malaysia',
  },
  licenseNumber: 'HC-2022-0156',
  licenseExpiryDate: new Date('2026-12-31'),
  accreditationBody: 'JAKIM',
  serviceAreas: [
    IndustrySector.FOOD_MANUFACTURING,
    IndustrySector.FOOD_SERVICE,
    IndustrySector.LOGISTICS,
  ],
  activeClients: 12,
  completedAudits: 48,
  successRate: 94.5,
  createdAt: new Date('2022-01-15'),
  updatedAt: new Date('2025-12-28'),
  createdBy: 'user-004',
};

/**
 * Mock Certification Body
 */
export const mockCertificationBody: CertificationBody = {
  id: 'org-003',
  name: 'Majlis Agama Islam Selangor (MAIS)',
  type: OrganizationType.CERTIFICATION_BODY,
  status: OrganizationStatus.ACTIVE,
  registrationNumber: 'MAIS-1950',
  industry: IndustrySector.FOOD_MANUFACTURING,
  yearEstablished: 1950,
  primaryContact: {
    name: 'En. Mohd Fadzil bin Hassan',
    position: 'Head of Halal Certification Division',
    email: 'halal@mais.gov.my',
    phone: '+60355148000',
    mobile: '+60123456700',
  },
  businessAddress: {
    street: 'Tingkat 10, Wisma MAIS',
    city: 'Shah Alam',
    state: 'Selangor',
    postalCode: '40000',
    country: 'Malaysia',
  },
  authorityNumber: 'JAKIM-CB-001',
  accreditationStandard: 'MHMS 2020',
  jurisdictionCountries: ['Malaysia'],
  jurisdictionStates: ['Selangor'],
  certificatesIssued: 892,
  activeCertificates: 245,
  auditorsCount: 18,
  createdAt: new Date('2020-01-01'),
  updatedAt: new Date('2025-12-30'),
  createdBy: 'system',
};

/**
 * All mock organizations
 */
export const mockOrganizations: Organization[] = [
  mockCompany,
  mockConsultancy,
  mockCertificationBody,
];

export const mockCompanies: Company[] = [mockCompany];
export const mockConsultancies: Consultancy[] = [mockConsultancy];
export const mockCertificationBodies: CertificationBody[] = [mockCertificationBody];

export function getOrganizationById(id: string): Organization | undefined {
  return mockOrganizations.find((org) => org.id === id);
}

export function getCompanyById(id: string): Company | undefined {
  return mockCompanies.find((company) => company.id === id);
}

export function getConsultancyById(id: string): Consultancy | undefined {
  return mockConsultancies.find((consultancy) => consultancy.id === id);
}

export function getCertificationBodyById(id: string): CertificationBody | undefined {
  return mockCertificationBodies.find((cb) => cb.id === id);
}
